import { Suspense } from 'react';
import { Spinner, Stack } from '@customerio/pluma-components/react';
import { channelValueOf } from '@/features/deliveries';
import type { Delivery } from '@/services/db/delivery/entity';
import { useDelivery, useDeliveryDetailContent } from '@/services/db/delivery/queries';
import { DeliveryDetailMetadata } from './components/delivery-detail-metadata';
import { DeliveryMessagePreview } from './components/delivery-message-preview';
import { DeliveryPreviewHeader } from './components/delivery-preview-header';
import { InAppConfig } from './components/in-app-config';
import { InboxConfig } from './components/inbox-config';

type DeliveryDetailPageProps = {
  /** Delivery id from the route (`/journeys/outbox/deliveries/:deliveryId`). */
  deliveryId: string;
};

/**
 * Delivery detail: metadata (campaign / broadcast / action / newsletter /
 * transactional related-model links), the in-app / inbox config panels, and the
 * rendered message, from the existing `services/db` data layer. Still deferred:
 * related deliveries and draft edit mode; route wiring lands with the Outbox
 * page (FPS-2591).
 */
export function DeliveryDetailPage({ deliveryId }: DeliveryDetailPageProps) {
  return (
    <Suspense fallback={<Spinner />}>
      <DeliveryDetailContent deliveryId={deliveryId} />
    </Suspense>
  );
}

type DeliveryDetailContentProps = { deliveryId: string };

function DeliveryDetailContent({ deliveryId }: DeliveryDetailContentProps) {
  const { data: delivery } = useDelivery({ id: deliveryId });

  return (
    <Stack gap="300">
      <DeliveryDetailMetadata delivery={delivery} />
      <Suspense fallback={<Spinner />}>
        <DeliveryPreview deliveryId={deliveryId} delivery={delivery} />
      </Suspense>
    </Stack>
  );
}

type DeliveryPreviewProps = { deliveryId: string; delivery: Delivery };

/**
 * The channel config panel (in-app / inbox only) + preview header + rendered
 * message; all read the same suspended detail content. Ember renders the config
 * panels in the right-hand aside next to the metadata; the Hydra shell (FPS-2592)
 * ships a single column, so they render here in the main flow after the metadata
 * panel and before the message preview.
 */
function DeliveryPreview({ deliveryId, delivery }: DeliveryPreviewProps) {
  const { data: detail } = useDeliveryDetailContent({ id: deliveryId });
  const channel = channelValueOf(delivery.action_type);

  return (
    <>
      {channel === 'in_app' ? <InAppConfig detail={detail} /> : null}
      {channel === 'inbox' ? <InboxConfig detail={detail} /> : null}
      <DeliveryPreviewHeader delivery={delivery} detail={detail} />
      <DeliveryMessagePreview deliveryId={deliveryId} delivery={delivery} />
    </>
  );
}
